import appFeatures from "@/appFeatures";
import { SHARED_PRESERVED_KEYS, TAB_KEY_INFIX } from "@/store/tabStore/keys";
import { safe } from "@/store/tabStore/util";

import { v4 as uuidv4 } from "uuid";

/** Feature flag gate. When false, every transform below is a no-op passthrough. */
export const isTabIsolationEnabled = appFeatures.isOpenTabsOtherEnabled;

// `window.name` is a tab-owned string that survives refresh. We store the id
// behind a prefix so we never clobber (or get confused by) other uses of it.
const WINDOW_NAME_PREFIX = "lokasi:tabId=";
// sessionStorage is the recovery fallback if `window.name` gets reset
// (SSO redirect, target=_blank from another origin, extension interference).
const SESSION_STORAGE_KEY = "lokasi:tabId";

let cachedTabId: string | null = null;

const readFromWindowName = (): string | null => {
  if (typeof window === "undefined") return null;
  const name = window.name;
  return name.startsWith(WINDOW_NAME_PREFIX)
    ? name.slice(WINDOW_NAME_PREFIX.length)
    : null;
};

const writeWindowName = (id: string) => {
  if (typeof window !== "undefined") window.name = `${WINDOW_NAME_PREFIX}${id}`;
};

// sessionStorage may be unavailable (privacy mode); `safe` returns null and
// window.name still holds the id in that case.
const readSession = (): string | null =>
  safe(() => sessionStorage.getItem(SESSION_STORAGE_KEY));

const writeSession = (id: string): void => {
  safe(() => sessionStorage.setItem(SESSION_STORAGE_KEY, id));
};

/**
 * Resolve this tab's stable id: window.name -> sessionStorage -> new UUID.
 * Cached per tab and mirrored to both stores so a reset of either self-heals.
 */
export const resolveTabId = (): string => {
  if (cachedTabId) return cachedTabId;

  let id = readFromWindowName();
  if (id) {
    writeSession(id); // keep the fallback in sync
  } else {
    id = readSession();
    if (id) {
      writeWindowName(id); // restore primary from fallback
    } else {
      id = uuidv4();
      writeWindowName(id);
      writeSession(id);
    }
  }

  cachedTabId = id;
  return id;
};

/**
 * The id this load starts with, without minting one. Either our own id from before
 * a refresh, or the source tab's id (duplicating a tab clones both stores).
 */
export const peekInheritedTabId = (): string | null =>
  readFromWindowName() ?? readSession();

/** Drop the inherited id for a brand-new one (duplicate-tab fork). */
export const forkTabIdentity = (): string => {
  const id = uuidv4();
  writeWindowName(id);
  writeSession(id);
  cachedTabId = id;
  return id;
};

/** Clear tab identity (used when disabling the feature: ON -> OFF). */
export const resetTabIdentity = () => {
  cachedTabId = null;
  if (typeof window !== "undefined") window.name = "";
  safe(() => sessionStorage.removeItem(SESSION_STORAGE_KEY));
};

// ── Key classification ───────────────────────────────────────────────────

/** Shared keys are never namespaced (auth, react-query, internals). */
export const isSharedKey = (key: string): boolean =>
  SHARED_PRESERVED_KEYS.has(key) || key.startsWith("__");

/** True if a key already carries a tab namespace (`...:tab:<id>:...`). */
export const isTabScopedKey = (key: string): boolean =>
  key.includes(TAB_KEY_INFIX);

/** Extract the tabId embedded in a namespaced key, or null if there is none. */
export const tabIdFromKey = (key: string): string | null => {
  const idx = key.indexOf(TAB_KEY_INFIX);
  if (idx === -1) return null;
  const rest = key.slice(idx + TAB_KEY_INFIX.length);
  const end = rest.indexOf(":");
  return end === -1 ? null : rest.slice(0, end);
};

/** True if `key` is namespaced to exactly this `tabId`. */
export const keyBelongsToTab = (key: string, tabId: string): boolean =>
  key.includes(`${TAB_KEY_INFIX}${tabId}:`);

/** Rewrite a namespaced key into another tab's namespace (used when cloning). */
export const rekeyToTab = (
  key: string,
  fromTabId: string,
  toTabId: string
): string =>
  key.replace(`${TAB_KEY_INFIX}${fromTabId}:`, `${TAB_KEY_INFIX}${toTabId}:`);

/**
 * Namespace a key for the current tab: `persist:map` -> `persist:tab:<id>:map`
 * (unchanged when OFF, or for shared / already-namespaced keys). The `:tab:<id>:`
 * is inserted after the first `:` so prefixes (persist:, migration:) stay filterable.
 */
export const namespacedKey = (key: string): string => {
  if (!isTabIsolationEnabled) return key;
  if (isSharedKey(key) || isTabScopedKey(key)) return key;

  const id = resolveTabId();
  const sep = key.indexOf(":");
  if (sep === -1) return `tab:${id}:${key}`;
  return `${key.slice(0, sep)}${TAB_KEY_INFIX}${id}:${key.slice(sep + 1)}`;
};
